import { useEffect, useMemo, useRef, useState } from 'react';
import { User } from 'lucide-react';
import Modal from '@/components/ui/Modal';
import { useBulkMemberPhotos } from '@/hooks/useMemberWebcamPhotos';
import { chunkArray, useChunkedReveal } from '@/hooks/useChunkedReveal';
import { rankMembers } from '@/lib/member-search';
import type { MemberDirectoryEntry } from '@/types';

interface Props {
  members: MemberDirectoryEntry[] | undefined;
  query: string;
  onClose: () => void;
  onSelect: (member: MemberDirectoryEntry) => void;
}

function MemberPhoto({ url, name }: { url?: string | null; name: string }) {
  const [failed, setFailed] = useState(false);
  return url && !failed ? (
    <img src={url} alt={name} loading="lazy" decoding="async"
      onError={() => setFailed(true)} className="h-52 w-full object-contain bg-gray-100" />
  ) : (
    <div className="h-52 bg-gray-100 flex flex-col items-center justify-center gap-2 text-gray-500">
      <User className="h-16 w-16" aria-hidden="true" />
      <span className="text-sm">{failed ? 'Photo unavailable' : 'No photo available'}</span>
    </div>
  );
}

function ResultChunk({ members, onSelect }: Pick<Props, 'onSelect'> & { members: MemberDirectoryEntry[] }) {
  const ids = useMemo(() => members.map(member => member.id), [members]);
  const { data: photos, isLoading, error, refetch } = useBulkMemberPhotos(ids);
  return (
    <>
      {error && <div className="col-span-full text-sm text-amber-800" role="alert">
        Photos could not be loaded. <button type="button" className="underline" onClick={() => refetch()}>Retry</button>
      </div>}
      {members.map(member => {
        const name = `${member.first_name} ${member.last_name}`;
        const photo = photos?.[member.id];
        const url = photo?.large_photo_url ?? photo?.thumbnail_photo_url;
        return (
          <button key={member.id} type="button" onClick={() => onSelect(member)}
            aria-label={`Select ${name}`}
            className="overflow-hidden rounded-lg border border-gray-200 text-left hover:border-green-700 hover:shadow-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-green-700">
            {isLoading ? <div className="h-52 bg-gray-100 flex items-center justify-center text-sm text-gray-500">Loading photo…</div>
              : <MemberPhoto key={url ?? 'missing'} url={url} name={name} />}
            <div className="p-3 space-y-1">
              <p className="font-semibold text-gray-900 break-words">{name}</p>
              <p className="text-sm text-gray-500 break-all">{member.email}</p>
              {member.whatsapp_number && <p className="text-sm text-gray-500">{member.whatsapp_number}</p>}
            </div>
          </button>
        );
      })}
    </>
  );
}

export default function MemberSearchResultsModal({ members, query, onClose, onSelect }: Props) {
  const results = useMemo(() => rankMembers(members, query, Infinity).members, [members, query]);
  const chunks = useMemo(() => chunkArray(results, 24), [results]);
  const { visibleChunks, sentinelRef, hasMore } = useChunkedReveal(chunks.length, query);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const previous = document.activeElement as HTMLElement | null;
    const dialog = containerRef.current?.querySelector<HTMLElement>('[role="dialog"]');
    dialog?.querySelector<HTMLElement>('button')?.focus();
    const trapFocus = (event: KeyboardEvent) => {
      if (event.key !== 'Tab' || !dialog) return;
      const buttons = Array.from(dialog.querySelectorAll<HTMLElement>('button:not(:disabled), [href], input, [tabindex="0"]'));
      const first = buttons[0];
      const last = buttons[buttons.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault(); last?.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault(); first?.focus();
      }
    };
    document.addEventListener('keydown', trapFocus);
    return () => { document.removeEventListener('keydown', trapFocus); previous?.focus(); };
  }, []);

  return (
    <div ref={containerRef}>
      <Modal isOpen onClose={onClose} title="Matching members"
        subtitle={`${results.length} ${results.length === 1 ? 'member' : 'members'} matching “${query}”. Select a member to open check-in.`}
        className="max-w-5xl">
        {results.length === 0 ? <p className="py-12 text-center text-gray-500">No members match this search.</p> : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {chunks.slice(0, visibleChunks).map((chunk, index) => <ResultChunk key={index} members={chunk} onSelect={onSelect} />)}
          </div>
        )}
        {hasMore && <div ref={sentinelRef} role="status" className="py-6 text-center text-sm text-gray-500">Scroll to see more members…</div>}
      </Modal>
    </div>
  );
}
